import { ReactComponent as RelationshipsIcon } from "../../Assets/images/myRelationship.svg";
import { ReactComponent as MembershipIcon } from "../../Assets/images/subscription.svg";
import { ReactComponent as VoiceIcon } from "../../Assets/icon/voice.svg";
import { ReactComponent as FaithIcon } from "../../Assets/icon/prey.svg";
import { ReactComponent as NotificationsIcon } from "../../Assets/images/bellIcon.svg";
import { ReactComponent as SupportIcon } from "../../Assets/images/support.svg";
import { ReactComponent as SessionHistoryIcon } from "../../Assets/icon/ResumeCoaching.svg";
import { ReactComponent as PartnerChatIcon } from "../../Assets/images/partner_chat.svg";
import { splitGrants, availablePassCount } from "../../Services/shareAris";
import { getVoiceOptionByValueOrId } from "../../Utils/CommonFunctions/voiceMapping";
import { CheckCircle } from "../SettingsList";

/**
 * Single source of truth for the header menu navigation tree.
 *
 * The menu panel renders this with drill-down (matching the mobile Figma flow,
 * node 9012:697) and the standalone settings routes render the same rows inside
 * SettingsPageShell — so no screen is ever defined twice.
 * See docs/navigation-redesign-plan.md §3.3 and §8.
 *
 * Row shape:
 *   { id, label, value?, icon?, badge?, requiredFeature?,
 *     right?    — "chevron" (default) | "toggle" | "none"
 *     screen?   — drill down to SCREENS[screen] instead of navigating
 *     to?       — route path
 *     onClick?  — custom action (wins over `to`)
 *     checked?, onChange?  — toggle rows
 *     disabled? — visible but inert; used for rows whose backend does not exist
 *                 yet, so the IA is complete without faking persistence }
 */

// Conditional on corporate membership, which needs a member-facing endpoint that
// does not exist yet (GET /api/corporate/me/sponsorship — plan §5.5).
const EMPLOYER_SPONSORSHIP_ENABLED = false;

function EmployerIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect
        x="2.75"
        y="6.75"
        width="18.5"
        height="13.5"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M9 6.5V5.5A1.5 1.5 0 0110.5 4h3A1.5 1.5 0 0115 5.5v1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="9" cy="12.5" r="1.75" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M6 17.25c.4-1.4 1.55-2.25 3-2.25s2.6.85 3 2.25M14.5 12h3.5M14.5 15.5h3.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Stroke-drawn so it follows currentColor on both the blue panel and white cards.
function ConnectedHealthIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 20s-7.5-4.35-7.5-10A4.25 4.25 0 0112 7.3 4.25 4.25 0 0119.5 10c0 5.65-7.5 10-7.5 10z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M4.5 12.5h4l1.5-2.5 2.5 5 1.5-2.5h5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AdminIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65a.5.5 0 00.12-.64l-2-3.46a.5.5 0 00-.61-.22l-2.49 1a7.03 7.03 0 00-1.69-.98l-.38-2.65a.5.5 0 00-.49-.42h-4a.5.5 0 00-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1a.5.5 0 00-.61.22l-2 3.46a.5.5 0 00.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98L2.46 14.63a.5.5 0 00-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.22.09.49 0 .61-.22l2-3.46a.5.5 0 00-.12-.64l-2.11-1.65z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// --- Value formatters --------------------------------------------------------

const NOT_ADDED = "Not Added";

function fullName(profile) {
  return `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim();
}

function membershipStatusValue({ subscription, subscriptionLoaded }) {
  if (!subscriptionLoaded) return null;
  if (!subscription) return NOT_ADDED;

  // Shape mirrors ManageMembershipNew.jsx:344-350 — the unified /subscriptions
  // endpoint and the legacy payload nest status differently.
  const record = subscription?.subscriptions?.[0] || subscription;
  const legacy = subscription?.subscription_data;
  const isActive = record?.status === "active" || legacy?.status === "active";
  const isCancelling =
    record?.cancel_at_period_end === true || legacy?.cancel_at_period_end === true;

  if (isActive && isCancelling) return "Active · cancels at period end";
  if (isActive) return "Active";
  return record?.status || legacy?.status || NOT_ADDED;
}

function voiceMinutesValue({ coachingMinutes, unlimitedCoaching, accessLoaded }) {
  if (!accessLoaded) return null;
  if (unlimitedCoaching) return "Unlimited";
  if (coachingMinutes == null) return NOT_ADDED;
  return `${coachingMinutes} min`;
}

function companionValue({ grants, grantsLoaded }) {
  if (!grantsLoaded) return null;
  const { companion } = splitGrants(grants);
  if (!companion) return NOT_ADDED;
  return companion.status === "active" ? "Active" : "Invitation sent";
}

function passesValue({ grants, grantsLoaded }) {
  if (!grantsLoaded) return null;
  const count = availablePassCount(grants);
  if (!count) return "None available";
  return `${count} ${count === 1 ? "Pass" : "Passes"}`;
}

const FAITH_LEVEL_LABEL = {
  minimal: "Minimal",
  moderate: "Moderate",
  deep: "Deep",
};

function preferredVoiceValue({ profile }) {
  const raw = profile?.aris_voice;
  if (!raw) return NOT_ADDED;
  return getVoiceOptionByValueOrId(raw)?.value || raw;
}

// --- Drill-down screens -----------------------------------------------------

export const SCREENS = {
  myProfile: {
    title: "My Profile",
    // Every field is edited on /myprofile, which already owns the flows for
    // email/phone re-verification.
    build: ({ profile }) => [
      {
        id: "profile-photo",
        label: "Profile Photo",
        value: profile?.profile_pic_url ? "Added" : NOT_ADDED,
        to: "/myprofile",
      },
      {
        id: "name",
        label: "Name",
        value: fullName(profile) || NOT_ADDED,
        to: "/myprofile",
      },
      {
        id: "email",
        label: "Email Address",
        value: profile?.email || NOT_ADDED,
        to: "/myprofile",
      },
      {
        id: "mobile",
        label: "Mobile Number",
        value: profile?.mobile || NOT_ADDED,
        to: "/myprofile",
      },
      { id: "password", label: "Password & Login", to: "/myprofile" },
    ],
  },

  membership: {
    title: "Membership",
    build: (ctx) => [
      {
        id: "membership-status",
        label: "Membership Status",
        value: membershipStatusValue(ctx),
        to: "/manageMembership",
      },
      {
        id: "voice-minutes",
        label: "Voice Minutes Remaining",
        value: voiceMinutesValue(ctx),
        to: "/manageMembership",
      },
      {
        id: "companion",
        label: "Companion Membership",
        value: companionValue(ctx),
        to: "/share-aris",
      },
      {
        id: "passes",
        label: "Aris Passes",
        value: passesValue(ctx),
        to: "/share-aris",
      },
      {
        id: "billing",
        label: "Billing & Payment",
        onClick: ctx.openBillingPortal,
      },
    ],
  },

  employerSponsorship: {
    title: "Employer Sponsorship",
    build: ({ sponsorship, sponsorshipLoaded }) => [
      {
        id: "organization",
        label: "Organization",
        value: sponsorshipLoaded ? sponsorship?.organization || NOT_ADDED : null,
        right: "none",
      },
      {
        id: "work-email",
        label: "Workplace Verification Email",
        value: sponsorshipLoaded ? sponsorship?.work_email || NOT_ADDED : null,
        right: "none",
      },
      {
        id: "verification",
        label: "Verification Status",
        value: !sponsorshipLoaded ? null : sponsorship?.verification_status ===
          "verified" ? (
          <>
            <CheckCircle />
            Verified
          </>
        ) : (
          sponsorship?.verification_status || "Unverified"
        ),
        right: "none",
      },
      {
        id: "sponsored-through",
        label: "Sponsored Through",
        value: sponsorshipLoaded ? sponsorship?.sponsored_through || "—" : null,
        right: "none",
      },
      { id: "update-work-email", label: "Update Workplace Email", disabled: true },
    ],
  },

  voiceLanguage: {
    title: "Voice & Language",
    build: (ctx) => [
      {
        id: "preferred-voice",
        label: "Preferred Voice",
        value: preferredVoiceValue(ctx),
        to: "/voice-preference",
      },
      {
        id: "default-language",
        label: "Default Language",
        // The language Aris converses in (core-lambda menu node, aris-ai's record):
        // English, or the member's own language when they spoke it with Aris.
        value: ctx.languageLoaded ? ctx.language?.label || "English" : null,
        right: "none",
      },
      // One row per other offered language: tapping switches to it (plan 46 #8).
      ...((ctx.languageLoaded && ctx.language?.options) || [])
        .filter((option) => option.value && option.value !== ctx.language?.value)
        .map((option) => ({
          id: `language-${option.value}`,
          label: `Switch to ${option.label || option.value}`,
          onClick: () => ctx.chooseLanguage(option.value),
        })),
    ],
  },

  faith: {
    title: "Faith & Spirituality",
    // Two settings only (product 2026-10-15): the faith and how deeply Aris
    // integrates it, read from aris-ai's faith profile. Prayer & Reflection
    // Preferences and Reminder Settings were removed.
    build: ({ faith, faithLoaded }) => [
      {
        id: "faith-selection",
        label: "Faith Selection",
        value: faithLoaded ? faith?.faith || NOT_ADDED : null,
        to: "/faith",
      },
      {
        id: "integration-level",
        label: "Integration Level",
        value: faithLoaded && faith?.level ? FAITH_LEVEL_LABEL[faith.level] || faith.level : null,
        to: "/faith",
      },
    ],
  },

  notifications: {
    title: "Notifications",
    // Only push_notifications and email_comm exist on user_settings today. The
    // rest are rendered so the IA is complete, but stay disabled until the
    // schema extension in plan §5.3 lands — a toggle that silently fails to
    // persist would be worse than one that is visibly not ready.
    build: ({ notifications, notificationsLoaded, setNotification }) => {
      const toggle = (id, label, field, { pending = false } = {}) => ({
        id,
        label,
        right: "toggle",
        checked: field ? !!notifications?.[field] : false,
        disabled: pending || !notificationsLoaded,
        onChange: field ? (next) => setNotification(field, next) : undefined,
      });

      return [
        toggle("daily-check-ins", "Daily Check-ins", null, { pending: true }),
        toggle("journey-reminders", "Journey Reminders", null, { pending: true }),
        toggle("calendar-reminders", "Calendar & Event Reminders", null, {
          pending: true,
        }),
        toggle("push", "Push Notifications", "push_notifications"),
        toggle("email", "Email Notifications", "email_comm"),
        toggle("sms", "SMS Notifications", null, { pending: true }),
      ].filter(Boolean);
    },
  },

  support: {
    title: "Support",
    // /customer-support is a free-text message form plus the support email and
    // phone number, so it genuinely serves all three contact intents. Help
    // Center and FAQs need real content before they can go anywhere.
    build: () => [
      { id: "help-center", label: "Help Center", disabled: true },
      { id: "contact-support", label: "Contact Support", to: "/customer-support" },
      { id: "report-issue", label: "Report an Issue", to: "/customer-support" },
      { id: "suggest-feature", label: "Suggest a Feature", to: "/customer-support" },
      { id: "faqs", label: "FAQs", disabled: true },
    ],
  },
};

// --- Root menu --------------------------------------------------------------

export function buildMenuTree(ctx) {
  const identity = {
    id: "my-profile",
    label: fullName(ctx.profile) || "My Profile",
    value: ctx.profile?.email,
    screen: "myProfile",
  };

  const primary = [
    {
      id: "my-relationships",
      label: "My Relationships",
      icon: <RelationshipsIcon />,
      to: "/manageRelationship",
      requiredFeature: "RELATIONSHIPS",
      badge: ctx.relationships.length,
    },
    // These three are not in the new Figma menu IA. They were dropped in the
    // redesign on the plan that the home page would carry them, but the new Aris
    // tab is a two-button Voice/Text chooser with nowhere to put them — so they
    // were left unreachable while their pages stayed built and routed. Restored
    // here until Journey's recent-activity work can absorb Session History.
    // "Your Elements" was removed from the menu (2026-08-13): the legacy
    // /elements page is superseded by Journey's elements explorer, and
    // assessment-taking on web funnels to the app via the Your Elements
    // quick-item flow (DomainAssessmentView's download-the-app message).
    {
      id: "session-history",
      label: "Session History",
      icon: <SessionHistoryIcon />,
      to: "/coachingHistory",
      requiredFeature: "COACHING",
    },
    {
      id: "dialogues",
      label: "Aris Dialogues for Two",
      icon: <PartnerChatIcon />,
      // Navigates rather than opening the partner picker from the menu, as the
      // old row did. /dialogues owns that modal already — including its
      // desktop/mobile split — and gates it behind checkActivationAndProceed,
      // which the menu path bypassed.
      to: "/dialogues",
      requiredFeature: "DIALOGS_FOR_TWO",
    },
    {
      id: "membership",
      label: "Membership",
      icon: <MembershipIcon />,
      screen: "membership",
      requiredFeature: "SUBSCRIPTION",
    },
    EMPLOYER_SPONSORSHIP_ENABLED && {
      id: "employer-sponsorship",
      label: "Employer Sponsorship",
      icon: <EmployerIcon />,
      screen: "employerSponsorship",
    },
    {
      id: "voice-language",
      label: "Voice & Language",
      icon: <VoiceIcon />,
      screen: "voiceLanguage",
    },
    {
      id: "faith",
      label: "Faith & Spirituality",
      icon: <FaithIcon />,
      screen: "faith",
    },
    {
      // Read-and-manage only on web: Apple Health / Health Connect connect from
      // the phone; this lists what is connected and lets the member manage it.
      id: "connected-health",
      label: "Connected Health",
      icon: <ConnectedHealthIcon />,
      to: "/connected-health",
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: <NotificationsIcon />,
      screen: "notifications",
    },
    {
      id: "support",
      label: "Support",
      icon: <SupportIcon />,
      screen: "support",
    },
    {
      id: "admin",
      label: "Admin",
      icon: <AdminIcon />,
      to: "/admin",
      requiredFeature: "ADMIN",
    },
  ].filter(Boolean);

  const legal = [
    { id: "terms", label: "Member Agreement", to: "/term-of-use" },
    { id: "privacy", label: "Privacy Policy", to: "/privacy-policy" },
  ];

  const danger = [
    {
      id: "logout",
      label: "Log Out",
      right: "none",
      danger: true,
      onClick: ctx.openLogout,
    },
  ];

  return { identity, primary, legal, danger };
}

/**
 * Rows visible when onboarding is incomplete. Matches the pre-existing gate in
 * MenuComponent: legal + logout only, no identity card, no primary navigation.
 */
export function buildOnboardingMenuTree(ctx) {
  const { legal, danger } = buildMenuTree(ctx);
  return { identity: null, primary: [], legal, danger };
}
